import { cellForWeek, WeekStore } from "./useWeekMenuStore"
import { cellStateFor } from "./weekMenuLib"
import { RecentDish, ThemeDay, WeekDishRow, WeekMenuCellState, WeekMenuDayState } from "./weekMenuTypes"

function cellIsStarted(week: WeekStore, row: WeekDishRow, dayIndex: number): boolean {
    const cell = cellForWeek(week, row, dayIndex)
    const state = cellStateFor(row, cell.values, cell.isTouched)
    return state === WeekMenuCellState.FILLED || state === WeekMenuCellState.NEEDS_FILL
}

/** Unique dishes already entered this week, for the "previous menus" title suggestions. */
export function collectRecentDishes(week: WeekStore, rows: ReadonlyArray<WeekDishRow>): ReadonlyArray<RecentDish> {
    const byTitle = new Map<string, RecentDish>()
    rows.forEach((row) => {
        row.availableDayIndexes.forEach((dayIndex) => {
            const values = cellForWeek(week, row, dayIndex).values
            const title = values.title.trim()
            if (title === "" || byTitle.has(title.toLowerCase())) return
            byTitle.set(title.toLowerCase(), {
                title,
                description: values.description,
                price: values.price,
                allergens: values.allergens,
            })
        })
    })
    return Array.from(byTitle.values())
}

export type DayFillProgress = Readonly<{ filledCount: number; totalCount: number }>

/** Fill progress for one day over the template cells available on that day. */
export function countDayFillProgress(
    week: WeekStore,
    rows: ReadonlyArray<WeekDishRow>,
    dayIndex: number,
): DayFillProgress {
    let filledCount = 0
    let totalCount = 0
    rows.forEach((row) => {
        if (!row.availableDayIndexes.includes(dayIndex)) return
        totalCount += 1
        const cell = cellForWeek(week, row, dayIndex)
        if (cellStateFor(row, cell.values, cell.isTouched) === WeekMenuCellState.FILLED) filledCount += 1
    })
    return { filledCount, totalCount }
}

/** Fill progress for a theme day: a theme dish counts as filled once it has a title. */
export function countThemeFillProgress(theme: ThemeDay): DayFillProgress {
    const filledCount = theme.dishes.filter((dish) => dish.values.title.trim() !== "").length
    return { filledCount, totalCount: theme.dishes.length }
}

/** Fill progress for one dish row across the days it is available. */
export function countRowFillProgress(week: WeekStore, row: WeekDishRow): DayFillProgress {
    let filledCount = 0
    row.availableDayIndexes.forEach((dayIndex) => {
        const cell = cellForWeek(week, row, dayIndex)
        if (cellStateFor(row, cell.values, cell.isTouched) === WeekMenuCellState.FILLED) filledCount += 1
    })
    return { filledCount, totalCount: row.availableDayIndexes.length }
}

/** Open days whose menu is complete: every template slot filled, or a theme with every dish titled. */
export function countReadyDays(
    week: WeekStore,
    rows: ReadonlyArray<WeekDishRow>,
    days: ReadonlyArray<WeekMenuDayState>,
): Readonly<{ readyCount: number; dayCount: number }> {
    let readyCount = 0
    let dayCount = 0
    days.forEach((dayState, dayIndex) => {
        if (dayState.isClosed) return
        dayCount += 1
        if (dayState.theme) {
            const { filledCount, totalCount } = countThemeFillProgress(dayState.theme)
            if (totalCount > 0 && filledCount === totalCount) readyCount += 1
        } else {
            const { filledCount, totalCount } = countDayFillProgress(week, rows, dayIndex)
            if (totalCount > 0 && filledCount === totalCount) readyCount += 1
        }
    })
    return { readyCount, dayCount }
}

/** Dishes the kitchen has started on a day, used by the close-day and theme confirmations. */
export function startedCellCountForDay(week: WeekStore, rows: ReadonlyArray<WeekDishRow>, dayIndex: number): number {
    return rows.filter((row) => row.availableDayIndexes.includes(dayIndex) && cellIsStarted(week, row, dayIndex)).length
}
